import mixpanel, { Dict } from 'mixpanel-browser'
import { isWebView, trackAndroidEvent } from 'utils/android'
import { waitForInjection } from 'services/Api/apiHelper'
import { IPlatform } from './Mixpanel.types'
import { FirebaseAnalytics } from './FirebaseAnalytics'

// make env_check = true while developing to check the events
const env_check = process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'development'

const getCurrentTime = () => ~~(Date.now() / 1000)

// Queue to store events fired before MixPanel is enabled, replayed once enabled
const pendingEvents: Array<{ event_name: string; properties?: Dict }> = []

const actions = {
  // Current platform
  platform: IPlatform.DESKTOP,
  // Whether Mixpanel event sending is enabled (controlled by is_mixpanel feature flag)
  isMixpanelEnabled: false,
  // Stored Mixpanel key for deferred initialization
  mixpanelKey: '',
  // Set the platform to be used
  setPlatform(platform: IPlatform) {
    this.platform = platform
  },
  // Set whether Mixpanel event sending is enabled
  setMixpanelEnabled(enabled: boolean) {
    this.isMixpanelEnabled = enabled
    if (enabled && actions.mixpanelKey && env_check && !isWebView()) {
      mixpanel.init(actions.mixpanelKey)
    }
  },
  // Replay queued events that were fired before MixPanel was enabled
  // Should be called after identify/register so replayed events have user identity
  flushPendingEvents() {
    while (pendingEvents.length > 0) {
      const event = pendingEvents.shift()!
      mixpanel.track(event.event_name, event.properties)
    }
  },
  // Store the mixpanel key for deferred initialization (init happens when AB flag enables Mixpanel)
  init: (key: string) => {
    actions.mixpanelKey = key
  },
  // Identify a user with a unique ID to track user activity across devices
  identify(id: string) {
    if (env_check) {
      if (!isWebView()) {
        if (this.isMixpanelEnabled) {
          mixpanel.identify(id)
        }
        FirebaseAnalytics.setUserId(id)
      }
    }
  },
  reset() {
    if (env_check) {
      if (!isWebView()) {
        if (this.isMixpanelEnabled) {
          mixpanel.reset()
        }
        FirebaseAnalytics.setUserId('')
      }
    }
  },
  // To register super properties
  register(props: Dict) {
    if (env_check) {
      if (!isWebView()) {
        if (this.isMixpanelEnabled) {
          mixpanel.register(props)
        }
        const stringProps: Record<string, string> = {}
        Object.keys(props).forEach((key) => {
          stringProps[key] = String(props[key])
        })
        FirebaseAnalytics.setUserProperties(stringProps)
      }
    }
  },
  // To Track events
  track(event_name: string, properties?: Dict) {
    if (env_check) {
      if (window.androidObj && window.androidObj.trackEvent) {
        window.androidObj.trackEvent(event_name, JSON.stringify(properties))
      } else {
        if (this.isMixpanelEnabled) {
          mixpanel.track(event_name, properties)
        } else if (!isWebView()) {
          pendingEvents.push({ event_name, properties })
        }
        FirebaseAnalytics.logEvent(event_name, properties)
      }
    }
  },
  // To track events with some preset values (Take out from common usecase to avoid repetition)
  async trackWithPreset(event_name: string, properties?: Dict) {
    if (env_check) {
      const mixpanelProps = {
        ...properties,
        time: getCurrentTime(),
        Platform: this.platform,
        Lang: localStorage.getItem('i18nextLng'),
        Version: 'V2'
      }
      await waitForInjection()
      const isMixpanelEnabled = this.isMixpanelEnabled

      trackAndroidEvent(event_name, mixpanelProps, () => {
        if (isMixpanelEnabled) {
          mixpanel.track(event_name, mixpanelProps)
        } else if (!isWebView()) {
          pendingEvents.push({ event_name, properties: mixpanelProps })
        }
        FirebaseAnalytics.logEvent(event_name, mixpanelProps)
      })
    }
  },
  // To set a group of people in a set
  people: {
    set: (props: Dict) => {
      if (env_check && !isWebView() && actions.isMixpanelEnabled) {
        mixpanel.people.set(props)
      }
    }
  }
}

export const Mixpanel = actions
